/**
 * @file        src/pages/testimonial/AddTestimonial.jsx
 * @description Create / edit a testimonial: name, company, designation, phone,
 *              email, star rating, status, language, image upload, description.
 *              Reused for /testimonials/new and /testimonials/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-03
 * @changelog
 *   - 2026-09-15 Claude — Language is English-only and reads "English", not "en".
 *                          The dropdown offered en / hi / mr for a storefront that
 *                          serves English only, so a testimonial saved as hi/mr was
 *                          content nobody could read. The list now comes from
 *                          lib/languages.js — one file for all five content forms.
 *   - 2026-08-06 Claude — Validate description length client-side too (max 600,
 *                          matching the textarea and the API): the zod schema
 *                          never checked it, so an existing over-limit value
 *                          (e.g. loaded from an old record) could be resaved
 *                          unchanged instead of surfacing an error.
 *   - 2026-08-04 Claude — Video URL field (CR A3): external https link only
 *                          (YouTube/Vimeo/file); the image doubles as poster.
 *   - 2026-07-03 Claude — Initial (Testimonial Management).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../lib/languages';

const schema = z.object({
  name: z.string().trim().min(1, 'Required').max(150),
  company: z.string().trim().max(190).optional(),
  designation: z.string().trim().max(150).optional(),
  phone: z.string().trim().max(30).optional(),
  email: z.string().trim().email('Invalid email').max(190).optional().or(z.literal('')),
  star: z.coerce.number().int().min(1).max(5),
  language: z.string().trim().min(2).max(8),
  is_active: z.boolean(),
  video_url: z.string().trim().url('Must be a full https:// link').max(500)
    .startsWith('https://', 'Must be a full https:// link').optional().or(z.literal('')),
  description: z.string().trim().max(600, 'Keep it under 600 characters').optional().or(z.literal('')),
});

const empty = {
  name: '', company: '', designation: '', phone: '', email: '',
  star: 5, image_url: '', video_url: '', description: '', language: DEFAULT_LANGUAGE, is_active: true,
};

const STAR_OPTS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n} Star${n > 1 ? 's' : ''}` }));

export default function AddTestimonial() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: qk.testimonials.one(id),
    queryFn: () => get(`/testimonials/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const t = existing.data;
      setForm({
        name: t.name || '', company: t.company || '', designation: t.designation || '',
        phone: t.phone || '', email: t.email || '', star: t.star || 5,
        image_url: t.image_url || '', video_url: t.video_url || '',
        description: t.description || '',
        language: t.language || DEFAULT_LANGUAGE, is_active: !!t.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (b) => (isEdit ? patch(`/testimonials/${id}`, b) : post('/testimonials', b)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Testimonial updated' : 'Testimonial added', `${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['testimonials'] });
      navigate('/testimonials');
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    const body = {
      name: parsed.data.name,
      star: parsed.data.star,
      language: parsed.data.language,
      is_active: parsed.data.is_active,
    };
    if (parsed.data.company) body.company = parsed.data.company;
    if (parsed.data.designation) body.designation = parsed.data.designation;
    if (parsed.data.phone) body.phone = parsed.data.phone;
    if (parsed.data.email) body.email = parsed.data.email;
    if (form.image_url) body.image_url = form.image_url;
    body.video_url = parsed.data.video_url || '';
    if (form.description) body.description = form.description;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Testimonial' : 'Add Testimonial'}
        subtitle="Testimonial Management"
        actions={<button className="btn ghost" onClick={() => navigate('/testimonials')}><Icon name="x" /> Cancel</button>}
      />
      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Testimonial Name" required err={errors.name}>
            <input value={form.name} onChange={on('name')} placeholder="Full name" />
          </Field>
          <Field label="Company Name" err={errors.company}>
            <input value={form.company} onChange={on('company')} placeholder="Company" />
          </Field>
          <Field label="Designation" err={errors.designation}>
            <input value={form.designation} onChange={on('designation')} placeholder="e.g. CEO" />
          </Field>

          <Field label="Phone" err={errors.phone}>
            <input value={form.phone} onChange={on('phone')} placeholder="Phone number" />
          </Field>
          <Field label="Email" err={errors.email}>
            <input value={form.email} onChange={on('email')} placeholder="email@example.com" />
          </Field>
          <Field label="Star" required err={errors.star}>
            <Select
              value={form.star}
              onChange={(v) => setForm({ ...form, star: Number(v) })}
              options={STAR_OPTS}
            />
          </Field>

          <Field label="Status">
            <Select
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(v) => setForm({ ...form, is_active: v === 'active' })}
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
            />
          </Field>
          <Field label="Language" required err={errors.language}>
            <Select
              value={form.language}
              onChange={(v) => setForm({ ...form, language: v })}
              options={LANGUAGE_OPTIONS}
            />
          </Field>
          <Field label="Image">
            <ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
          </Field>

          {/* External link only (product decision — no video uploads). The image
              above doubles as the poster frame on the public video card. */}
          <Field label="Video URL" err={errors.video_url}
            hint="Optional. YouTube / Vimeo / direct video link (https). The image is used as the poster.">
            <input value={form.video_url} onChange={on('video_url')} placeholder="https://www.youtube.com/watch?v=…" />
          </Field>

          {/* Capped in the UI: the API accepts 4,000 characters, and a testimonial
              anywhere near that long turns its homepage card into a column of text
              taller than the section. A quote worth reading is a few sentences. */}
          <Field label="Description" full err={errors.description}>
            <textarea
              rows={6}
              maxLength={600}
              value={form.description}
              onChange={on('description')}
              placeholder="What the customer said…"
            />
            <small className="muted">
              {(form.description || '').length}/600 characters · shown on the homepage card
            </small>
          </Field>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Submit'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
